import { Box, CircularProgress, Stack, Typography, IconButton } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import { forwardRef, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { IconAddImages } from "../../../icons/IconAddImages";
import { COLORS } from "../../../styles/variables";
import { BoxImageStyle, BoxInputStyle, IconButtonStyle, LoadingRowStyle } from "./style";
import { IconCloseRed } from "../../../icons/IconCloseRed";

interface ImageField {
    id: string;
    url: string;
    file?: File;
    mediaId?: number;
}

export type ImageUploaderItem = { mediaId: number; url: string }

/** Full gallery from server of truth — replaces field array instead of appending upload URLs. */
export type ImageUploaderReplaceResult = {
    mode: 'replace'
    images: ImageUploaderItem[]
}

export type ImageUploaderFilesSelectedResult =
    | ImageUploaderItem[]
    | ImageUploaderReplaceResult
    | void

interface ImageUploaderProps {
    name?: string;
    onBlur?: () => void;
    onFilesSelected?: (files: File[]) => Promise<ImageUploaderFilesSelectedResult> | ImageUploaderFilesSelectedResult;
    onRemoveImage?: (image: { mediaId?: number; url: string }) => Promise<void> | void;
    hideDropZone?: boolean;
    emptyPlaceholder?: string;
    /** Notifies parent when upload/remove is in progress (e.g. to disable "Добавить"). */
    onBusyChange?: (busy: boolean) => void;
}

const isReplaceResult = (value: unknown): value is ImageUploaderReplaceResult =>
    Boolean(value && typeof value === 'object' && (value as ImageUploaderReplaceResult).mode === 'replace')

export const ImageUploader = forwardRef<HTMLInputElement, ImageUploaderProps>(
    ({ name, onBlur, onFilesSelected, onRemoveImage, hideDropZone = false, emptyPlaceholder, onBusyChange }, ref) => {
    const { control, setError, clearErrors } = useFormContext();
    const blobUrlsRef = useRef<Set<string>>(new Set());
    const fieldName = name || "images";
    const [isUploading, setIsUploading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const isBusy = isUploading || isRemoving;

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: fieldName,
    });

    useEffect(() => {
        onBusyChange?.(isBusy);
    }, [isBusy, onBusyChange]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || isBusy) return;

        const fileArray = Array.from(files);
        const currentCount = fields.length;
        const maxFiles = 10;
        const maxFileSize = 5 * 1024 * 1024; // 5 MB

        // Check if already at limit
        if (currentCount >= maxFiles) {
            setError(fieldName, {
                type: "manual",
                message: "Максимум 10 изображений"
            });
            toast.error("Максимум 10 изображений");
            e.target.value = "";
            return;
        }

        const availableSlots = maxFiles - currentCount;
        let oversizedFilesCount = 0;
        let canAddCount = 0;

        // First pass: validate all files and count issues
        fileArray.forEach((file) => {
            if (file.size > maxFileSize) {
                oversizedFilesCount++;
            } else if (canAddCount < availableSlots) {
                canAddCount++;
            }
        });

        // Show single error message (prioritize size over count)
        if (oversizedFilesCount > 0) {
            setError(fieldName, {
                type: "manual",
                message: "Размер каждого файла не должен превышать 5 МБ"
            });
            toast.error("Размер каждого файла не должен превышать 5 МБ");
        } else if (fileArray.length > canAddCount) {
            setError(fieldName, {
                type: "manual",
                message: "Максимум 10 изображений"
            });
            toast.error("Максимум 10 изображений");
        }

        // Second pass: add valid files
        let actualAddedCount = 0;
        const validFiles: File[] = [];
        fileArray.forEach((file) => {
            if (file.size <= maxFileSize && validFiles.length < availableSlots) {
                validFiles.push(file);
            }
        });

        if (validFiles.length > 0) {
            if (onFilesSelected) {
                setIsUploading(true);
                try {
                    const uploaded = await onFilesSelected(validFiles);
                    if (isReplaceResult(uploaded)) {
                        replace(uploaded.images.map((u) => ({ url: u.url, mediaId: u.mediaId })));
                        actualAddedCount = Math.max(uploaded.images.length, validFiles.length);
                    } else if (Array.isArray(uploaded) && uploaded.length > 0) {
                        uploaded.forEach((u) => append({ url: u.url, mediaId: u.mediaId }));
                        actualAddedCount = uploaded.length;
                    }
                } catch {
                    toast.error("Не удалось загрузить изображения")
                } finally {
                    setIsUploading(false);
                }
            } else {
                validFiles.forEach((file) => {
                    const previewUrl = URL.createObjectURL(file);
                    blobUrlsRef.current.add(previewUrl);
                    append({ url: previewUrl, file: file });
                    actualAddedCount++;
                });
            }
        }

        // Clear errors if we successfully added files and no longer at limit
        if (actualAddedCount > 0 && oversizedFilesCount === 0 && currentCount + actualAddedCount < maxFiles) {
            clearErrors(fieldName);
        }

        e.target.value = "";
    };

    const handleRemove = async (index: number) => {
        if (isBusy) return;
        const field = fields[index] as ImageField;
        setIsRemoving(true);
        try {
            if (onRemoveImage) {
                await onRemoveImage({ mediaId: field.mediaId, url: field.url });
            }

            if (field.url && field.url.startsWith('blob:')) {
                URL.revokeObjectURL(field.url);
                blobUrlsRef.current.delete(field.url);
            }
            remove(index);
        } catch {
            toast.error("Не удалось удалить изображение")
        } finally {
            setIsRemoving(false);
        }

        // Clear errors after removal if we're no longer at limit
        if (fields.length - 1 < 10) {
            clearErrors(fieldName);
        }
    };

    // Cleanup blob URLs on unmount
    useEffect(() => {
        const currentBlobUrls = blobUrlsRef.current;
        return () => {
            currentBlobUrls.forEach(url => {
                URL.revokeObjectURL(url);
            });
            currentBlobUrls.clear();
        };
    }, []);

    const fileInput = (
        <input
            ref={ref}
            name={fieldName}
            onBlur={onBlur}
            type="file"
            multiple
            accept="image/*"
            hidden
            disabled={isBusy}
            onChange={handleFileChange}
        />
    )

    return (
        <Stack gap={2} width="100%">
            {hideDropZone && fileInput}
            {isUploading && (
                <Stack direction="row" alignItems="center" gap={1.5} sx={LoadingRowStyle}>
                    <CircularProgress size={20} />
                    <Typography variant="black-14-normal">Загрузка изображений…</Typography>
                </Stack>
            )}
            {hideDropZone && fields.length === 0 && !isUploading && emptyPlaceholder ? (
                <Typography variant="black-14-normal" mt={3}>
                    {emptyPlaceholder}
                </Typography>
            ) : (
                <Stack direction="row" flexWrap="wrap" gap={2} mt={isUploading ? 0 : 3} sx={{ opacity: isBusy ? 0.7 : 1 }}>
                    {fields.map((field, index) => {
                        const imageField = field as ImageField;
                        return (
                        <Box key={imageField.id} sx={BoxImageStyle}>
                            <img
                                src={imageField.url}
                                alt="preview"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <IconButton
                                onClick={() => handleRemove(index)}
                                size="small"
                                disabled={isBusy}
                                sx={IconButtonStyle}
                            >
                                <IconCloseRed fontSize="small" />
                            </IconButton>
                        </Box>
                        );
                    })}
                </Stack>
            )}

            {!hideDropZone && (
                <Box
                    component="label"
                    sx={{
                        ...BoxInputStyle,
                        pointerEvents: isBusy ? 'none' : 'auto',
                        opacity: isBusy ? 0.7 : 1,
                    }}
                >
                    {fileInput}
                    {isUploading ? (
                        <>
                            <CircularProgress size={28} sx={{ mb: 1 }} />
                            <Typography variant="black-16-semiBold" sx={{ color: COLORS.CERULEAN }}>
                                Загрузка изображений…
                            </Typography>
                        </>
                    ) : (
                        <>
                            <IconAddImages />
                            <Typography variant="black-16-semiBold" sx={{ color: COLORS.CERULEAN }}>
                                Нажмите, чтобы загрузить фото
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                PNG, JPG до 5МБ
                            </Typography>
                        </>
                    )}
                </Box>
            )}
        </Stack>
    );
});

ImageUploader.displayName = "ImageUploader";
